import Image from 'next/future/image';
import { Breakpoints } from 'settings/breakpoints';
import useSWR from 'swr';
import { extractTags } from '@lambda/reebelo/src/tagHelpers';
import { uniqBy } from 'lodash-es';
import { SearchOffers } from '@lambda/commons/apis/elasticsearch/types';
import { useContext } from 'react';
import { AppCtx } from 'pages/_app';
import { CartContext } from 'pages/cart';
import cn from 'classnames';
import Link from 'next/link';
import { CtaLink } from '@/components/Cta/Cta';
import Loading from '@/components/Loading/Loading';
import Slider from '@/components/slider/Slider';
import {
  handleAddToCart,
  makeRecommendedItemsSeq,
} from '../cart/recommendedItemsUtils';
import { priceFormater } from '@/settings/index';
import useCart, {
  generateAddCartDateTimeAttr,
  generateCategoryAttr,
} from '@/lib/use-cart';
import useAttributes from '@/lib/use-attributes';
import { ItemTypeWithProps, QtyComponentProps } from '../cart/types';
import { RenderQtyComponent } from '../cart/RecommendedItem';
import { getOfferUri } from '@/lib/offer';
import Section from '../commons/Section';

const BREAKPOINTS = {
  [Breakpoints.NONE]: {
    slidesPerView: 1.25,
    slidesPerGroup: 1,
    spaceBetween: 10,
  },
  [Breakpoints.MD]: {
    slidesPerView: 1.5,
    slidesPerGroup: 1,
    spaceBetween: 10,
  },
  [Breakpoints.LG]: {
    slidesPerView: 2,
    slidesPerGroup: 2,
    spaceBetween: 10,
  },
  [Breakpoints.XL]: {
    slidesPerView: 3,
    slidesPerGroup: 3,
    spaceBetween: 10,
  },
};

const RecommendedItem = ({ offer }: { offer: SearchOffers; index: number }) => {
  const { cart } = useContext(AppCtx);
  const lineItems = cart?.lines.edges.map((item) => item.node);
  const lineItem = lineItems?.find(
    (item) => item.merchandise.sku === offer.sku,
  );

  const itemQuantity = lineItem?.quantity;
  const selectedMerchId = lineItem?.id;

  const { loading, add, setScrollPosOnCartChange, update, remove } =
    useContext(CartContext);

  const CartHelpers = useCart();

  const {
    deliveryAttributes,
    shippingAttributes,
    subsidyAttributes,
    vendorAttributes,
    offerUppricerMetaAttributes,
    categoryPkAttributes,
  } = useAttributes({ product: offer, type: 'offer' } as ItemTypeWithProps);

  const { cartCount: cartSize } = CartHelpers;

  const itemCategory = offer.productType || offer.categories?.[0];

  const offerSpecs = `${offer.color} | ${offer.condition}`;
  const offerTitle = offer.title.replace(
    `- ${offer.color} - ${offer.condition}`,
    '',
  );

  return (
    <div className="relative">
      <Link href={getOfferUri(offer)} key={offer.key}>
        <a target="_blank" rel="noopener noreferrer">
          <div className="flex h-full flex-col justify-between rounded-md border p-3 text-xs hover:border-black sm:p-5">
            <div className="flex">
              <div className="flex w-full sm:w-5/6">
                <div className="mr-5 flex h-[50px] w-[50px] items-center justify-center">
                  <span className="h-[50px] w-[50px]">
                    <Image
                      src={offer.imageSrc}
                      width={50}
                      height={50}
                      alt={offer.title}
                      className="h-full w-[50px] object-contain"
                    />
                  </span>
                </div>
                <div>
                  <h2 className="text-left font-bold">{offerTitle}</h2>
                  <span className="mt-1 block">{offerSpecs}</span>
                </div>
              </div>
              <div className="hidden sm:block sm:w-1/6">
                <p className="mb-2 w-full text-right font-bold">
                  {priceFormater.long(offer.price)}
                </p>
              </div>
            </div>
            <div className="mt-3 flex min-h-[32px] items-center justify-between sm:mt-0 sm:justify-end">
              <p className="font-bold sm:hidden">
                {priceFormater.long(offer.price)}
              </p>
            </div>
          </div>
        </a>
      </Link>
      <div className="absolute right-3 bottom-3 sm:right-5 sm:bottom-5">
        {itemQuantity && itemQuantity > 0 ? (
          <RenderQtyComponent
            {...({
              update,
              remove,
              product: offer,
              loading,
              itemPrice: offer.price,
              itemQuantity,
              type: 'offer',
              selectedMerchId,
              lineItem,
            } as QtyComponentProps)}
          />
        ) : (
          <div
            className={cn(
              'group flex h-8 items-center justify-center rounded-md border border-gray-700 px-3 transition duration-150 ease-in-out ',
              {
                'opacity-50': loading,
                'hover:bg-gray-700 hover:text-white': !loading,
              },
            )}
          >
            <CtaLink
              variant="white"
              target="_blank"
              className={cn(
                '!flex !h-full !w-full !items-center !justify-center !bg-transparent !text-xs !shadow-none',
                {
                  '!cursor-not-allowed': loading,
                  '!cursor-pointer group-hover:!text-white': !loading,
                },
              )}
              onClick={(e) => e.preventDefault()}
              onClickCapture={handleAddToCart({
                add,
                loading,
                variantId: offer.variantId,
                type: 'offer',
                title: offer.title,
                price: offer.price,
                vendor: offer.vendor,
                itemPsku: offer.psku,
                itemSku: offer.sku,
                setScrollPosOnCartChange,
                attributes: [
                  ...vendorAttributes,
                  ...subsidyAttributes,
                  ...shippingAttributes,
                  ...deliveryAttributes,
                  ...generateAddCartDateTimeAttr(),
                  ...generateCategoryAttr(itemCategory),
                  ...offerUppricerMetaAttributes,
                  ...categoryPkAttributes,
                ],
                quantity: 1,
                itemCondition: offer.condition,
                itemColor: offer.color,
                itemCategory,
                itemStorage: offer.storage,
                cartSize,
                itemBrand: offer.brand,
              })}
            >
              Add
            </CtaLink>
          </div>
        )}
      </div>
    </div>
  );
};

const makeLineItemsQS = (tags: string[]): string => {
  const { psku, category } = extractTags(tags);
  const isSmartphone = category === 'Smartphones';
  const isTablet = category === 'Tablets';
  const isLaptop = category === 'Laptops';
  const isComputer = category === 'Desktop Computer';
  const isEarphone = category === 'Earphones';
  const shouldLookForRecommendations =
    isSmartphone || isTablet || isComputer || isLaptop || isEarphone;

  if (!psku || !shouldLookForRecommendations) return '';

  return `&pskus=${psku}`;
};

const RecommendedAccessories = ({
  tags,
  isAccount,
}: {
  tags: string[];
  isAccount?: boolean;
}) => {
  const recommendedItemsQS = makeLineItemsQS(tags);
  const fetcher = (url: string) => fetch(url).then((res) => res.json());
  const { data: recommendations, error: recommendationsError } = useSWR(
    `/api/cart/recommended-offers?${recommendedItemsQS}`,
    fetcher,
  );

  const recommendationsLoading = !recommendations && !recommendationsError;
  const CartHelpers = useCart();

  if (recommendationsLoading) {
    return (
      <Section className="py-10">
        <Loading />
      </Section>
    );
  }

  const recommendedProducts = recommendations?.[0]?.recommendations
    ? uniqBy(
        makeRecommendedItemsSeq(recommendations[0].recommendations),
        'title',
      )
    : [];

  if (recommendedProducts.length < 3) return null;

  return (
    <CartContext.Provider value={CartHelpers}>
      <Section className={cn({ 'py-0': isAccount, 'py-8': !isAccount })}>
        {isAccount ? (
          <h3 className="text-lg font-semibold md:text-xl">
            Related to your order
          </h3>
        ) : (
          <h3 className="flex items-center text-lg font-semibold md:text-xl">
            Pair it with
          </h3>
        )}
        <div className=" mt-3 sm:mt-4 md:mt-5">
          <div className="relative">
            <Slider breakpoints={BREAKPOINTS} className="relative" smallArrow>
              {recommendedProducts.map((offer, i) => (
                <RecommendedItem
                  offer={offer}
                  index={i}
                  key={`${offer.key}-${i}`}
                />
              ))}
            </Slider>
          </div>
        </div>
      </Section>
    </CartContext.Provider>
  );
};

export default RecommendedAccessories;
